import { useIntersectionObserver } from '@cian/react-utils';
import { CustomTooltip } from '@cian/ui-kit';
import * as React from 'react';

import { ReplyOnboardingTooltipContent } from './ReplyOnboardingTooltipContent';
import { useMessageContext } from '../../utils/messageContext';
import { useReplyOnboardingContext } from '../../utils/replyOnboardingContext';

interface IReplyOnboardingTooltipProps {
  /** Сообщение — таргет онбординга (последнее свайпабельное) */
  isOnboardingTarget: boolean;
}

export const ReplyOnboardingTooltip: React.FC<React.PropsWithChildren<IReplyOnboardingTooltipProps>> = ({
  isOnboardingTarget,
  children,
}) => {
  const { complete, onOnboardingShown } = useReplyOnboardingContext();
  const { messageRef } = useMessageContext();

  /**
   * Вешаем обсервер на сообщение.
   * При уходе сообщения из viewport закрываем онбординг.
   */
  const { subscribeState } = useIntersectionObserver({
    elementRef: messageRef,
    initSubscribeAvailable: isOnboardingTarget,
    deps: [isOnboardingTarget, complete],
    onOutViewport: ({ intersectionsCount }) => {
      if (intersectionsCount.inView >= 1) {
        complete();
      }
    },
  });

  React.useEffect(() => {
    if (isOnboardingTarget) {
      subscribeState.setSubscribeAvailable();
    } else {
      subscribeState.setSubscribeUnavailable();
    }
  }, [isOnboardingTarget, subscribeState]);

  React.useEffect(() => {
    if (!isOnboardingTarget) {
      return;
    }

    onOnboardingShown();
  }, [onOnboardingShown, isOnboardingTarget]);

  if (!isOnboardingTarget) {
    return children;
  }

  return (
    <>
      {children}
      <CustomTooltip
        open
        arrow
        theme="black"
        placement="right"
        maxWidth={348}
        preventOverflow={false}
        anchorRef={messageRef}
        content={<ReplyOnboardingTooltipContent onClose={complete} />}
      />
    </>
  );
};
